/**
 * Settings configuration for Climadiag Commune
 * Technical configuration: indicators metadata, color scales, formatting, helper functions
 *
 * @file Technical configuration and data processing settings
 */

import { indicatorsContent } from './texts.js';
import _indicatorsConfiguration from './indicatorsConfiguration.js';
import indicatorsMetadata from '../../data/texts/indicators/index.json' with { type: 'json' };

// Re-export text content for backward compatibility
export {
  homePage,
  characteristicsText,
  risksText,
  getRisksHeading,
  getTerritoryLabel,
  chartLegend,
  print,
  printLastPage,
  indicatorsContent,
} from './texts.js';

// Re-export extracted modules for backward compatibility
export { greyColor, indicatorsColorScales } from './colorScales.js';
export { indicatorsFormatting, tooltipTitle } from './indicatorsFormatting.js';
export { default as indicatorsConfiguration } from './indicatorsConfiguration.js';
export { default as contentHierarchy } from './contentHierarchy.js';
export { default as OVERSEAS_TERRITORIES } from './overseasTerritories.js';

/**
 * Reference periods for climate data by domain
 * @type {Object}
 */
export const REFERENCE_PERIODS = {
  /** France hexagonale reference period (TRACC-2023) */
  HEXAGONAL: '1976-2005',
  /** Overseas territories reference period (SocleOM-Climat-2025) */
  OVERSEAS: '1991-2020',
};

/**
 * Retrieves the complete content of an indicator by its key
 *
 * @param {string} key - Indicator key (e.g. 'G1', 'R3', etc.)
 * @returns {Object|null} Indicator content object
 */
export const getIndicatorContent = (key) =>
  indicatorsContent.find(ind => ind.key === key) || null;

/**
 * Retrieves the description of an indicator for a given domain
 * With automatic fallback to the default description
 *
 * @param {string} key - Indicator key (e.g. 'G1', 'R3', etc.)
 * @param {string|undefined} domain - Value of entity.domaine
 * @returns {Array<Array<string>>} Description for the requested domain
 */
export const getIndicatorDescription = (key, domain) => {
  const content = getIndicatorContent(key);
  if (!content || !content.description || !Array.isArray(content.description)) {
    // Fallback: return generic message for all 3 outlooks if no description exists
    const genericText = `Les informations pour cet indicateur seront bientôt disponibles.`;
    return [[genericText], [genericText], [genericText]];
  }

  // Find description variant matching the domain
  if (domain) {
    const variant = content.description.find(desc =>
      desc.domains && desc.domains.includes(domain)
    );
    if (variant) {
      return variant.byOutlook || [];
    }
  }

  // Fallback to default
  const defaultVariant = content.description.find(desc =>
    desc.domains && desc.domains.includes('default')
  );

  return defaultVariant?.byOutlook || [];
};

/**
 * Retrieves the full title of an indicator
 * Falls back to metadata if content is not available
 *
 * @param {string} key - Indicator key
 * @returns {string} Indicator title
 */
export const getIndicatorTitle = (key) => {
  const content = getIndicatorContent(key);
  if (content?.title) {
    return content.title;
  }

  // Fallback to metadata for indicators without generated content
  return indicatorsMetadata.indicators[key]?.title || '';
};

/**
 * Retrieves the navigation title of an indicator
 * Falls back to metadata if content is not available
 *
 * @param {string} key - Indicator key
 * @returns {string} Indicator navigation title
 */
export const getIndicatorNavigation = (key) => {
  const content = getIndicatorContent(key);
  if (content?.navigation) {
    return content.navigation;
  }

  // Fallback to metadata for indicators without generated content
  return indicatorsMetadata.indicators[key]?.navigation || '';
};

/**
 * Checks if an indicator has a specific description for a given domain
 *
 * @param {string} key - Indicator key
 * @param {string|undefined} domain - Value of entity.domaine ('Réunion', 'Mayotte', etc.)
 * @returns {boolean} true if a specific description exists for this domain
 */
export const hasSpecificDescription = (key, domain) => {
  if (!domain) return false;

  const content = getIndicatorContent(key);
  if (!content?.description || !Array.isArray(content.description)) {
    return false;
  }

  return content.description.some(desc =>
    desc.domains && desc.domains.includes(domain)
  );
};

/**
 * Retrieves the technical configuration of an indicator
 *
 * @param {string} key - Indicator key (e.g. 'G1', 'R3', etc.)
 * @returns {Object|null} Indicator configuration object or null if not found
 */
export const getIndicatorConfiguration = (key) =>
  _indicatorsConfiguration[key] || null;

export default {
  indicatorsConfiguration: _indicatorsConfiguration,
};
